/* eslint-disable react/no-unused-prop-types */
import { connect } from 'react-redux';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getActEventAppendicesToTheProtocolOfActionBlockId } from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmFormDocument } from 'KnmPackage/KnmDocumentsModule/interfaces/IKnmFormDocument';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { getDecisionFormRef } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';

import { ErknmActEventItemBodyProtocolsAppendicesToTheProtocolOfActionDocument } from './ErknmActEventItemBodyProtocolsAppendicesToTheProtocolOfActionDocument';
import styles from './ErknmActEventItemBodyProtocolsAppendicesToTheProtocolOfActionDocument.module.scss';
import { ReactQuillEditorField } from '../../../../../../../UIPackage/MainModule/components/ReactQuillEditorField';
import { checkHasErrorInCollapseBlock } from '../../../../../../../functions/checkHasErrorInCollapseBlock';

interface IErknmActEventItemBodyProtocolsAppendicesToTheProtocolOfActionProps {
  watchOnly: boolean;
  name: string;
  eventId: string;
  isDecision?: boolean;
}

interface IErknmActEventItemBodyProtocolsAppendicesToTheProtocolOfActionStoreProps {
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
}

const ErknmActEventItemBodyProtocolsAppendicesToTheProtocolOfActionWithoutStore = ({
  watchOnly,
  name,
  formRef,
  eventId,
}: IErknmActEventItemBodyProtocolsAppendicesToTheProtocolOfActionProps &
  IErknmActEventItemBodyProtocolsAppendicesToTheProtocolOfActionStoreProps) => {
  const [knmCollapseOpened, setKnmCollapseOpened] = useState<{ [key: string]: boolean }>({});
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const addEventAppendicesToTheProtocolOfAction = useCallback(() => {
    if (!watchOnly && push) {
      const id = uuidv4();
      const addingEventInspector: IKnmFormDocument = {
        guid: id,
        active: null,
        correlationToken: null,
        description: '',
        listId: id,
        isNew: true,
      };

      push(name, addingEventInspector);
    }
  }, [push, watchOnly]);

  const makeRemoveEventProtocolDocHandler = (index: number) => () => {
    if (!watchOnly && remove) {
      remove(name, index);
    }
  };

  return (
    <FieldBlock
      label={'Приложения к протоколу действия'}
      className={styles.GroupMargin}
      button={
        <Button
          className="shared-btn-add"
          text="Добавить"
          variant="link"
          color="primary"
          type="button"
          id="ErknmActEventItemBodyProtocolsAppendicesToTheProtocolOfActionAddButton"
          disabled={watchOnly}
          onClick={addEventAppendicesToTheProtocolOfAction}
        />
      }
      labelId={`${eventId}.appendicesToTheProtocolOfAction`}
    >
      <FieldArray name={name}>
        {({ fields }: FieldArrayRenderProps<IKnmFormDocument, HTMLElement>) => {
          if (!fields.length) {
            return null;
          }

          return fields.map((inspectorFieldName, sIndex) => {
            const fieldValue: IKnmFormDocument = fields.value?.[sIndex];
            const id = getActEventAppendicesToTheProtocolOfActionBlockId(eventId, fieldValue.guid || '');

            const hasError = checkHasErrorInCollapseBlock(formRef, inspectorFieldName);

            return (
              <KnmCollapse
                id={id}
                theme="fields"
                title={`Приложение №${sIndex + 1}`}
                isNew={fieldValue.isNew}
                opened={fieldValue.isNew}
                onClose={!watchOnly ? makeRemoveEventProtocolDocHandler(sIndex) : undefined}
                key={id}
                changeOpenedCallback={e =>
                  setKnmCollapseOpened(state => ({
                    ...state,
                    [inspectorFieldName]: e,
                  }))
                }
                error={hasError && knmCollapseOpened[inspectorFieldName]}
              >
                <>
                  <ErknmActEventItemBodyProtocolsAppendicesToTheProtocolOfActionDocument
                    formRef={formRef}
                    name={`${inspectorFieldName}.attachments`}
                    watchOnly={watchOnly}
                  />
                  <FieldBlock id={`${inspectorFieldName}.description`} label={'Описание'} margin="top">
                    <ReactQuillEditorField
                      id={`${inspectorFieldName}.descriptionReactQuillEditor`}
                      name={`${inspectorFieldName}.descriptionReactQuillEditor`}
                      placeholder="Введите описание"
                      disabled={watchOnly}
                      handleCustomChange={(e: string) => {
                        // @ts-ignore
                        formRef?.current?.change(`${inspectorFieldName}.description`, e);
                      }}
                    />
                  </FieldBlock>
                </>
              </KnmCollapse>
            );
          });
        }}
      </FieldArray>
    </FieldBlock>
  );
};

const mapStateToProps = (
  state: IRootState,
  ownProps: IErknmActEventItemBodyProtocolsAppendicesToTheProtocolOfActionProps,
): IErknmActEventItemBodyProtocolsAppendicesToTheProtocolOfActionStoreProps => ({
  formRef: ownProps?.isDecision ? getDecisionFormRef(state) : getKnmFormRef(state),
});

export const ErknmActEventItemBodyProtocolsAppendicesToTheProtocolOfAction = connect(mapStateToProps)(
  ErknmActEventItemBodyProtocolsAppendicesToTheProtocolOfActionWithoutStore,
);
